import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { logger } from 'modules/logger';
import type { ApolloQueryResult } from '@apollo/client';
import { GET_HANGING_PROTOCOL_MATCH } from 'modules/Apollo/queries';
import type {
  GetHangingProtocolMatchQuery,
  GetHangingProtocolMatchQueryVariables,
} from 'generated/graphql';
import type { WithApolloContext } from 'modules/Apollo/types';

export type UseHangingProtocolMatchProps = {
  worklistId: Nullable<string>;
  studyIds: ReadonlyArray<string>;
  skippedStudyIds: ReadonlyArray<string>;
  includeAutoloadedPriors: boolean;
} & WithApolloContext;

export type UseHangingProtocolMatchResult = {
  hangingProtocol: GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'];
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<GetHangingProtocolMatchQuery>>;
};

// The study set whose match failure was last logged, shared by every instance of the hook so a failure is logged once.
let lastLoggedFailureKey: string | null = null;

/** Test seam: module state outlives a test, so a failure logged in one would silence the same failure in the next. */
export const __resetLoggedMatchFailureForTest = (): void => {
  lastLoggedFailureKey = null;
};

/**
 * Standalone hanging-protocol match query. Extracted out of GBVD so that
 * adding/removing priors fires only this small query (plus a per-study
 * fetch via `useManualPriorStudies`) instead of re-running GBVD's massive
 * response.
 *
 * The HP response is small (a layout template + viewport configurations),
 * so its cache write does not block the main thread meaningfully even when
 * it re-runs on every prior change.
 */
export const useHangingProtocolMatch = ({
  worklistId,
  studyIds,
  skippedStudyIds,
  includeAutoloadedPriors,
  context,
}: UseHangingProtocolMatchProps): UseHangingProtocolMatchResult => {
  const variables = useMemo<GetHangingProtocolMatchQueryVariables>(
    () => ({
      worklistIds: worklistId != null ? [worklistId] : [],
      studyIds: studyIds as string[],
      skippedStudyIds: skippedStudyIds as string[],
      includeAutoloadedPriors,
    }),
    [worklistId, studyIds, skippedStudyIds, includeAutoloadedPriors]
  );

  const { data, loading, error, refetch } = useQuery<
    GetHangingProtocolMatchQuery,
    GetHangingProtocolMatchQueryVariables
  >(GET_HANGING_PROTOCOL_MATCH, {
    variables,
    skip: worklistId == null,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    context: {
      ...context,
      telemetry: {
        ...context?.telemetry,
        query: 'getHangingProtocolMatch',
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  // The last answer this case's match gave, kept for the moments the current variables have none: while a new study
  // set is in flight, and after it fails. Without it a re-fire reads `undefined` until it lands, and one that never
  // lands -- adding a prior made the server's Default HP fallback load that prior's stacks for longer than the 30s
  // upstream timeout, so every match for the new study set came back 503 -- left the viewer with no protocol for the
  // rest of the case: the previous step bar over a blank grid, and drops filed into a step nothing renders (VX-5407).
  // Scoped to the case so a navigation never shows the previous case's protocol.
  //
  // A ref, written after commit, rather than state: recording an answer must never schedule a render. As state it did,
  // once per new `data` reference, and wherever `useQuery` hands back a fresh `data` on every render that is a render
  // loop ("Maximum update depth exceeded" across every suite that mounts `useStudies` under the global Apollo mock).
  const lastAnswerRef = useRef<{
    worklistId: string;
    hangingProtocol: GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'];
  } | null>(null);
  useEffect(() => {
    if (worklistId == null || data == null) return;
    lastAnswerRef.current = {
      worklistId,
      hangingProtocol: data.matchHangingProtocolByWorklistsAndStudies,
    };
    // An answer ends the failure, so a later one for the same study set is logged again.
    lastLoggedFailureKey = null;
  }, [worklistId, data]);

  const lastAnswer = lastAnswerRef.current;
  const keptAnswer =
    data == null && lastAnswer != null && lastAnswer.worklistId === worklistId
      ? lastAnswer.hangingProtocol
      : undefined;

  // This failure used to be silent, which is why the incident behind VX-5407 left no trace in the frontend logs. Logged
  // once per study set rather than per hook instance: several components mount `useStudies`, so one failed match
  // reaches each of them.
  useEffect(() => {
    if (error == null) return;
    const failureKey = JSON.stringify(variables);
    if (failureKey === lastLoggedFailureKey) return;
    lastLoggedFailureKey = failureKey;
    logger.warn('[HP] hanging protocol match failed', {
      keptPreviousAnswer: lastAnswerRef.current?.worklistId === worklistId,
      statusCode:
        error.networkError != null && 'statusCode' in error.networkError
          ? error.networkError.statusCode
          : null,
    });
  }, [error, variables, worklistId]);

  return {
    // `data` decides, not the field: a `null` match is the server answering "no protocol", which must win over the
    // kept answer, and `??` would swap it for the protocol the case has just stopped matching.
    hangingProtocol: data != null ? data.matchHangingProtocolByWorklistsAndStudies : keptAnswer,
    loading,
    refetch,
  };
};
